import { Box } from '@mui/material';
import moment, { MomentInput } from 'moment';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import {
  LegacyAttachmentInput,
  LegacyAutocomplete,
  LegacyDateInput,
  LegacyMuiIconButton,
  LegacyMuiTextField,
} from '~/legacy-components/LegacyForms';

import { DynamicTableCellConfig, SelectValues } from '../types';
import { useSxStyles } from '../useStyles';

export const EditTable = ({
  editData,
  control,
}: {
  editData: DynamicTableCellConfig[][];
  control: Control;
}) => {
  const sxStyles = useSxStyles();
  const columnComponent = (column: DynamicTableCellConfig, index: number) => {
    const getSelectValue = (value: string | number): SelectValues | undefined =>
      column.options
        ? column.options.find(
            (selectValue: SelectValues) => selectValue.value === value,
          )
        : undefined;

    switch (column.type) {
      case 'input':
        return (
          <Controller
            control={control}
            name={column.name as string}
            {...column.controllerConfig}
            render={({ field: { onChange, value } }) => (
              <LegacyMuiTextField
                error={column.error ? column.error(index) : false}
                disabled={column.disabled}
                onChange={(e: {
                  target: { value: string | File | null | undefined };
                }) => column.onChange(index, onChange, e.target.value)}
                value={value}
                sx={{ width: column.width }}
              />
            )}
          />
        );
      case 'select':
        return (
          <Controller
            control={control}
            name={column.name as string}
            {...column.controllerConfig}
            render={({ field: { onChange, value } }) => (
              <LegacyAutocomplete
                disableClearable={!column.clearable}
                disabled={column.disabled}
                placeholder=" "
                style={{ width: column.width }}
                onChange={(_e, onChangeValue) =>
                  column.onChange(
                    index,
                    onChange,
                    onChangeValue ? onChangeValue.value : '',
                  )
                }
                value={getSelectValue(value) || null}
                options={column.options || []}
                renderInput={(params) => (
                  <LegacyMuiTextField
                    error={column.error ? column.error(index) : false}
                    {...params}
                  />
                )}
              />
            )}
          />
        );

      case 'date':
        return (
          <Controller
            control={control}
            name={column.name as string}
            {...column.controllerConfig}
            render={({ field: { onChange, value } }) => (
              <LegacyDateInput
                disabled={column.disabled}
                disableFuture={!!column.disableFuture}
                onChange={(v: MomentInput | null) =>
                  column.onChange(
                    index,
                    onChange,
                    !!v ? moment(v).format('YYYY-MM-DD').toString() : null,
                  )
                }
                value={value}
                error={column.error ? column.error(index) : false}
              />
            )}
          />
        );
      case 'attachment':
        return (
          <Controller
            control={control}
            name={column.name as string}
            {...column.controllerConfig}
            render={({ field: { onChange, value } }) => (
              <LegacyAttachmentInput
                onChange={(value: File | undefined) =>
                  column.onChange(index, onChange, value)
                }
                value={value}
                error={column.error ? column.error(index) : false}
              />
            )}
          />
        );
      case 'delete':
        return (
          <LegacyMuiIconButton
            sx={{ minWidth: '20px' }}
            onClick={() => column.onChange(index)}
          >
            <LegacyFAIcon FAIcon={LegacyFAIcons.Delete} sx={sxStyles.redIcon} />
          </LegacyMuiIconButton>
        );
    }
    return <></>;
  };
  return (
    <Box component={'tbody'}>
      {editData.map((row, rowIndex) => (
        <Box component={'tr'} key={row[0].key}>
          {row.map((column, columnIndex) => (
            <Box
              component={'td'}
              key={columnIndex}
              sx={{
                ...sxStyles.td,
                width: column.width,
              }}
            >
              {columnComponent(column, rowIndex)}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};
